import { useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import apiClient from "../services/apiClient";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  LockOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  MailOutlined,
  SecurityScanOutlined,
  SafetyCertificateOutlined,
  KeyOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { ThemeContext } from "../context/ThemeContext";
import "./ChangePassword.css";
import logoShort from "../assets/logoShort.png";
import { getPostLoginPath } from "../utils/roleUtils";
import { message } from "antd";

const ChangePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useContext(ThemeContext);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [roles, setRoles] = useState(location.state?.roles || []);
  const [formData, setFormData] = useState({
    email: location.state?.email || "",
    currentPassword: location.state?.currentPassword || "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});

  const baseUrl = process.env.REACT_APP_AUTH_URL;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Validation function
  const validateForm = () => {
    console.log("Validating form with data:", formData);
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }



    // New password validation
    if (!formData.newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters long";
    } else if (
      !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(
        formData.newPassword
      )
    ) {
      newErrors.newPassword =
        "Password must contain uppercase, lowercase, number, and special character";
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    console.log("Validation errors:", newErrors);
    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    console.log("Form is valid:", isValid);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form submitted, current form data:", formData);
    console.log("Current errors:", errors);

    // Validate form before submission
    if (!validateForm()) {
      console.log("Form validation failed");
      return;
    }

    console.log("Form validation passed, proceeding with submission");

    setIsLoading(true);

    try {
      const changePasswordData = {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      };

      console.log("Attempting password change with:", changePasswordData);
      console.log("Using base URL:", baseUrl);
      const token = localStorage.getItem("authToken");
      const response = await apiClient.post(
        `${baseUrl}/user/change-password`,
        changePasswordData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log("Password Change Response:", response.data);


      message.success("Password changed successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: isDark ? "dark" : "light",
      });

      setIsLoading(false);
      const destination = getPostLoginPath(roles);
      setTimeout(() => {
        navigate(destination, {
          state: {
            show2FAPopup: true,
            email: formData.email,
          },
        });
      }, 500);

      // Add a small delay to ensure the loading state is reset and toast is visible

    } catch (error) {
      console.error("Password Change Error:", error);
      setIsLoading(false);

      let errorMessage = "Failed to change password. Please try again.";

      if (error.response) {
        console.log("Error response data:", error.response.data);
        console.log("Error response status:", error.response.status);
        errorMessage = error.response.data.message || errorMessage;
      } else if (error.request) {
        console.log("Error request:", error.request);
        errorMessage = "Network error. Please check your connection.";
      } else {
        console.log("Error message:", error.message);
        errorMessage = "An error occurred. Please try again.";
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: isDark ? "dark" : "light",
      });
    }
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className="change-password-container">
      <div className="password-background">
        <div className="background-pattern"></div>
      </div>

      <div className="password-content">
        {/* Left Side - Security Hero Section */}
        <div className="password-hero">
          <div className="floating-elements">
            <div className="floating-element"></div>
            <div className="floating-element"></div>
            <div className="floating-element"></div>
            <div className="floating-element"></div>
          </div>

          <div className="hero-content">
            <h1 className="hero-title">Secure Your Account</h1>
            <p className="hero-subtitle">
              Update your password to ensure the highest level of security for
              your SimplyBond account. Choose a strong, unique password to
              protect your investments.
            </p>

            <div className="hero-features">
              <div className="hero-feature">
                <div className="hero-feature-icon">
                  <SafetyCertificateOutlined />
                </div>
                <span>Enhanced Security Protection</span>
              </div>
              <div className="hero-feature">
                <div className="hero-feature-icon">
                  <SecurityScanOutlined />
                </div>
                <span>Real-time Security Monitoring</span>
              </div>
              <div className="hero-feature">
                <div className="hero-feature-icon">
                  <KeyOutlined />
                </div>
                <span>Strong Password Requirements</span>
              </div>
              <div className="hero-feature">
                <div className="hero-feature-icon">
                  <CheckCircleOutlined />
                </div>
                <span>Instant Password Update</span>
              </div>
            </div>

            <div className="security-tips">
              <h3>Password Security Tips:</h3>
              <ul>
                <li>Use at least 8 characters</li>
                <li>Include uppercase and lowercase letters</li>
                <li>Add numbers and special characters</li>
                <li>Avoid common words or phrases</li>
                <li>Don't reuse passwords from other accounts</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Right Side - Form Section */}
        <div className="password-form-section">
          <div className="password-card">
            {/* Theme Toggle */}
            <button
              className="theme-toggle-btn"
              onClick={toggleTheme}
              title={`Switch to ${isDark ? "light" : "dark"} mode`}
            >
              {isDark ? "☀️" : "🌙"}
            </button>

            {/* Header */}
            <div className="password-header">
              <div className="logo-section">
                <img
                  src={logoShort}
                  alt="SimplyBond"
                  className="password-logo"
                />
                <h1 className="password-title">SimplyBond</h1>
              </div>
              <p className="password-subtitle">
                Change your password to keep your account secure
              </p>
            </div>

            {/* Form */}
            <form className="password-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">
                  <MailOutlined />
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`form-input ${errors.email ? "error" : ""}`}
                  placeholder="Enter your email address"
                  disabled
                />
                {errors.email && (
                  <div className="error-message">{errors.email}</div>
                )}
              </div>



              <div className="form-group">
                <label className="form-label">
                  <LockOutlined />
                  New Password
                </label>
                <div className="password-input-container">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    className={`form-input password-input ${errors.newPassword ? "error" : ""
                      }`}
                    placeholder="Enter your new password"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={toggleNewPasswordVisibility}
                  >
                    {showNewPassword ? (
                      <EyeInvisibleOutlined />
                    ) : (
                      <EyeOutlined />
                    )}
                  </button>
                </div>
                {errors.newPassword && (
                  <div className="error-message">{errors.newPassword}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  <LockOutlined />
                  Confirm New Password
                </label>
                <div className="password-input-container">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`form-input password-input ${errors.confirmPassword ? "error" : ""
                      }`}
                    placeholder="Confirm your new password"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={toggleConfirmPasswordVisibility}
                  >
                    {showConfirmPassword ? (
                      <EyeInvisibleOutlined />
                    ) : (
                      <EyeOutlined />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <div className="error-message">{errors.confirmPassword}</div>
                )}
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={isLoading}
                onClick={() => console.log("Button clicked!")}
              >
                {isLoading ? "Updating Password..." : "Change Password"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
